'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/inputs/bulk-action/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { BulkActionLocationSelect } from 'BomdisoftComponents/inputs/bulk-action/selects/location';
import { Select } from 'BomdisoftComponents/inputs/field/select/index';
import { LocationDetails } from 'BomdisoftComponents/types';
import { bulkAction, change, getLanguageFromLocale, keepCurrent, selectLocationT, undo } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { type ChangeEvent, useState } from 'react';

type BulkActionsLocationInputProps = {
  selectHandler: (location: LocationDetails | null) => void;
  placeholder?: string;
  noOptionsText?: string;
  className?: string;
};

type BulkActionChangeType = 'keep-current' | 'change';
type BulkAction = {
  id: BulkActionChangeType;
  name: string;
};

export const BulkActionsLocationInput = ({ selectHandler, placeholder, noOptionsText, className }: BulkActionsLocationInputProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(params?.lang as string) ?? 'english';
  const [selectedAction, setSelectedAction] = useState<BulkActionChangeType>('keep-current');
  const actions: BulkAction[] = [
    { id: 'keep-current', name: keepCurrent[language] },
    { id: 'change', name: change[language] },
  ];

  const reset = () => {
    setSelectedAction('keep-current');
    selectHandler(null);
  };

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedAction(event.target.value as BulkActionChangeType);
    selectHandler(null);
  };

  return (
    <div className={joinClasses('bomdisoft-bulk-action-container', className)}>
      <Select aria-label={bulkAction[language]} className='bomdisoft-bulk-action-action-select' onChange={handleChange} size='small' testId='select-bulk-action' value={selectedAction}>
        {actions.map(action => (
          <option key={action.id} value={action.id}>
            {action.name}
          </option>
        ))}
      </Select>
      {selectedAction === 'change' && (
        <>
          <BulkActionLocationSelect noOptionsText={noOptionsText} placeholder={placeholder || selectLocationT[language]} selectHandler={selectHandler} />
          <button className='bomdisoft-bulk-action-undo' onClick={reset} type='button'>
            {undo[language]}
          </button>
        </>
      )}
    </div>
  );
};
